/**
 * Storage Utility
 *
 * Provides safe localStorage operations with error handling.
 * All operations automatically handle JSON serialization/deserialization.
 *
 * Usage:
 *   import { saveToStorage, loadFromStorage, removeFromStorage } from '@/components/OliverBotV2/utils/storage.js'
 *
 *   // Save data
 *   saveToStorage('myKey', { x: 10, y: 20 })
 *
 *   // Load data
 *   const position = loadFromStorage('myKey', { x: 0, y: 0 })
 *
 *   // Remove data
 *   removeFromStorage('myKey')
 */

/**
 * Save data to localStorage
 * @param {string} key - The localStorage key
 * @param {*} value - The value to save (will be JSON.stringified)
 * @returns {boolean} - True if successful, false otherwise
 */
export function saveToStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Failed to save to localStorage [${key}]:`, error);
    return false;
  }
}

/**
 * Load data from localStorage
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value to return if key doesn't exist or parsing fails
 * @returns {*} - The parsed value or defaultValue
 */
export function loadFromStorage(key, defaultValue = null) {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
    return defaultValue;
  } catch (error) {
    console.warn(`Failed to load from localStorage [${key}]:`, error);
    return defaultValue;
  }
}

/**
 * Remove data from localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} - True if successful, false otherwise
 */
export function removeFromStorage(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove from localStorage [${key}]:`, error);
    return false;
  }
}

/**
 * Check if a key exists in localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} - True if key exists, false otherwise
 */
export function hasInStorage(key) {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.warn(`Failed to check localStorage [${key}]:`, error);
    return false;
  }
}

/**
 * Clear all data from localStorage
 * @returns {boolean} - True if successful, false otherwise
 */
export function clearStorage() {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.warn("Failed to clear localStorage:", error);
    return false;
  }
}
